import { useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calculator,
  History,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  LogOut,
  Cog,
  Table2,
  LayoutTemplate,
  Wrench,
  BookOpen,
  ScrollText,
  Activity,
  FileText,
  Building2,
  UserCheck,
  Mail,
  LifeBuoy,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LogissimoGradientBar } from "@/components/ui/logissimo-gradient-bar";
import { useAuthStore, useIsAdmin, useIsManager, useIsViewer } from "@/lib/auth/store";

import logoReserve from "@/assets/logo-reserve.png";
import logoRvb from "@/assets/logo-rvb.png";

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavSubSection {
  label: string;
  items: NavItem[];
}

interface NavSection {
  label?: string;
  items?: NavItem[];
  subSections?: NavSubSection[];
}

// Menu sections for admin users
const adminNavSections: NavSection[] = [
  {
    label: "Back-office",
    subSections: [
      {
        label: "Tracking / Reporting",
        items: [
          {
            title: "Dashboard",
            href: "/",
            icon: LayoutDashboard,
          },
          {
            title: "Toutes les simulations",
            href: "/admin/simulation-events",
            icon: Activity,
          },
          {
            title: "Toutes les cotations",
            href: "/admin/simulations",
            icon: History,
          },
          {
            title: "Toutes les propositions",
            href: "/admin/proposals",
            icon: FileText,
          },
          {
            title: "Suivi utilisateurs",
            href: "/admin/user-activity",
            icon: UserCheck,
          },
        ],
      },
      {
        label: "Configuration",
        items: [
          {
            title: "Configuration simulateurs",
            href: "/admin/simulators",
            icon: Cog,
          },
          {
            title: "Tables de référence",
            href: "/admin/references",
            icon: Table2,
          },
          {
            title: "Modèles",
            href: "/admin/models",
            icon: LayoutTemplate,
          },
          {
            title: "Utilitaires",
            href: "/admin/utilities",
            icon: Wrench,
          },
          {
            title: "Politique tarifaire",
            href: "/admin/pricing-policy",
            icon: BookOpen,
          },
        ],
      },
      {
        label: "Administration",
        items: [
          {
            title: "Utilisateurs",
            href: "/users",
            icon: Users,
          },
          {
            title: "Groupes (Régions)",
            href: "/admin/groups",
            icon: Users,
          },
          {
            title: "Agences",
            href: "/admin/agencies",
            icon: Building2,
          },
          {
            title: "Alertes & Mailing",
            href: "/admin/alerts",
            icon: Mail,
          },
        ],
      },
      {
        label: "Changelog",
        items: [
          {
            title: "Changelog",
            href: "/admin/changelog",
            icon: ScrollText,
          },
        ],
      },
      {
        label: "Aide",
        items: [
          {
            title: "Documentation",
            href: "/admin/documentation",
            icon: LifeBuoy,
          },
        ],
      },
    ],
  },
  {
    label: "Général",
    items: [
      {
        title: "Simulateurs",
        href: "/simulators",
        icon: Calculator,
      },
      {
        title: "Mon Historique",
        href: "/history",
        icon: History,
      },
      {
        title: "Politique tarifaire",
        href: "/pricing-policy",
        icon: BookOpen,
      },
      {
        title: "Paramètres",
        href: "/settings",
        icon: Settings,
      },
    ],
  },
];

// Menu sections for managers (Responsables)
const managerNavSections: NavSection[] = [
  {
    label: "Gestion",
    subSections: [
      {
        label: "Suivi",
        items: [
          {
            title: "Dashboard",
            href: "/",
            icon: LayoutDashboard,
          },
          {
            title: "Simulateurs",
            href: "/simulators",
            icon: Calculator,
          },
          {
            title: "Mon Historique",
            href: "/history",
            icon: History,
          },
        ],
      },
      {
        label: "Administration",
        items: [
          {
            title: "Utilisateurs",
            href: "/users",
            icon: Users,
          },
        ],
      },
    ],
  },
  {
    label: "Général",
    items: [
      {
        title: "Politique tarifaire",
        href: "/pricing-policy",
        icon: BookOpen,
      },
      {
        title: "Paramètres",
        href: "/settings",
        icon: Settings,
      },
    ],
  },
];

// Menu sections for standard users (BE) — reporting limited to their region
const userNavSections: NavSection[] = [
  {
    label: "Tracking / Reporting",
    subSections: [
      {
        label: "Suivi",
        items: [
          {
            title: "Dashboard",
            href: "/",
            icon: LayoutDashboard,
          },
          {
            title: "Toutes les simulations",
            href: "/admin/simulation-events",
            icon: Activity,
          },
          {
            title: "Toutes les cotations",
            href: "/admin/simulations",
            icon: History,
          },
          {
            title: "Toutes les propositions",
            href: "/admin/proposals",
            icon: FileText,
          },
        ],
      },
    ],
  },
  {
    label: "Général",
    items: [
      {
        title: "Simulateurs",
        href: "/simulators",
        icon: Calculator,
      },
      {
        title: "Mon Historique",
        href: "/history",
        icon: History,
      },
      {
        title: "Politique tarifaire",
        href: "/pricing-policy",
        icon: BookOpen,
      },
      {
        title: "Paramètres",
        href: "/settings",
        icon: Settings,
      },
    ],
  },
];

// Menu sections for viewers (consultants) — read-only access to reporting
const viewerNavSections: NavSection[] = [
  {
    label: "Tracking / Reporting",
    subSections: [
      {
        label: "Suivi",
        items: [
          {
            title: "Dashboard",
            href: "/",
            icon: LayoutDashboard,
          },
          {
            title: "Toutes les simulations",
            href: "/admin/simulation-events",
            icon: Activity,
          },
          {
            title: "Toutes les cotations",
            href: "/admin/simulations",
            icon: History,
          },
          {
            title: "Toutes les propositions",
            href: "/admin/proposals",
            icon: FileText,
          },
          {
            title: "Suivi utilisateurs",
            href: "/admin/user-activity",
            icon: UserCheck,
          },
        ],
      },
    ],
  },
  {
    label: "Général",
    items: [
      {
        title: "Simulateurs",
        href: "/simulators",
        icon: Calculator,
      },
      {
        title: "Mon Historique",
        href: "/history",
        icon: History,
      },
      {
        title: "Politique tarifaire",
        href: "/pricing-policy",
        icon: BookOpen,
      },
      {
        title: "Paramètres",
        href: "/settings",
        icon: Settings,
      },
    ],
  },
];

export function Sidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const logout = useAuthStore((state) => state.logout);
  const isAdmin = useIsAdmin();
  const isManager = useIsManager();
  const isViewer = useIsViewer();

  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const isPreviewMode = searchParams.get("preview") === "true";

  const navSections = isAdmin
    ? adminNavSections
    : isManager
      ? managerNavSections
      : isViewer
        ? viewerNavSections
        : userNavSections;

  return (
    <motion.aside
      initial={false}
      animate={{ width: isCollapsed ? 72 : 256 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="sticky top-0 relative flex flex-col h-screen bg-card border-r border-border"
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b border-border relative">
        <Link to="/" className="flex items-center gap-3">
          <img
            src={logoRvb}
            alt="Logissimo"
            className="h-8 object-contain dark:hidden"
          />
          <img
            src={logoReserve}
            alt="Logissimo"
            className="h-8 object-contain hidden dark:block"
          />
          <AnimatePresence>
            {!isCollapsed && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col"
              >
                <span className="font-semibold text-foreground text-sm leading-tight">Logissimo Pricemaker</span>
              </motion.div>
            )}
          </AnimatePresence>
        </Link>
        <LogissimoGradientBar 
          variant="subtle" 
          height="sm" 
          className="absolute bottom-0 left-0 right-0" 
        />
      </div>

      {/* Toggle button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-20 z-10 h-6 w-6 rounded-full border bg-card shadow-md hover:bg-accent"
      >
        {isCollapsed ? (
          <ChevronRight className="h-3 w-3" />
        ) : (
          <ChevronLeft className="h-3 w-3" />
        )}
      </Button>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="px-2">
          {navSections.map((section, sectionIndex) => {
            // Helper function to render nav items
            const renderNavItems = (items: NavItem[]) => (
              <div className="space-y-1">
                {items.map((item) => {
                  // In preview mode, highlight "Configuration simulateurs" instead of "Simulateurs"
                  const isActive =
                    item.href === "/"
                      ? pathname === "/"
                      : isPreviewMode && pathname.startsWith("/simulators/") && item.href === "/admin/simulators"
                        ? true
                        : isPreviewMode && pathname.startsWith("/simulators/") && item.href === "/simulators"
                          ? false
                          : pathname.startsWith(item.href);

                  const linkContent = (
                    <Link
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                        isActive
                          ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent"
                      )}
                    >
                      <item.icon className={cn("h-5 w-5 shrink-0")} />
                      <AnimatePresence>
                        {!isCollapsed && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="font-medium"
                          >
                            {item.title}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </Link>
                  );

                  if (isCollapsed) {
                    return (
                      <Tooltip key={item.href} delayDuration={0}>
                        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                        <TooltipContent side="right">{item.title}</TooltipContent>
                      </Tooltip>
                    );
                  }

                  return <div key={item.href}>{linkContent}</div>;
                })}
              </div>
            );

            return (
              <div key={sectionIndex}>
                {/* Section separator (not for first section) */}
                {sectionIndex > 0 && (
                  <div className="my-3 px-3">
                    <Separator />
                  </div>
                )}

                {/* Section label */}
                {section.label && !isCollapsed && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                  >
                    {section.label}
                  </motion.div>
                )}

                {/* Section with sub-sections */}
                {section.subSections && (
                  <div className="space-y-3">
                    {section.subSections.map((subSection, subIndex) => (
                      <div key={subIndex}>
                        {/* Sub-section label */}
                        {!isCollapsed && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="px-3 py-1.5 text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider"
                          >
                            {subSection.label}
                          </motion.div>
                        )}
                        {renderNavItems(subSection.items)}
                      </div>
                    ))}
                  </div>
                )}

                {/* Section items (for sections without sub-sections) */}
                {section.items && renderNavItems(section.items)}
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="p-2 border-t border-border">
        <Separator className="mb-2" />
        {isCollapsed ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className="w-full text-muted-foreground hover:text-destructive"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Déconnexion</TooltipContent>
          </Tooltip>
        ) : (
          <Button
            variant="ghost"
            onClick={logout}
            className="w-full justify-start text-muted-foreground hover:text-destructive"
          >
            <LogOut className="mr-3 h-5 w-5" />
            Déconnexion
          </Button>
        )}
      </div>
    </motion.aside>
  );
}
